/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Base logic for Listable
 */

import { type Bizo, Viewing, autoclass } from '@zcode/bizos'
import {
  type BuildingAmenity,
  type EnumTitle,
  type ListingAmenity,
  MediaType,
  PropertyFeatures,
} from '@zcode/enums'
import {
  mediaUrl,
  toBuildingShortUrl,
  toExternalBuildingUrl,
  toExternalListingUrl,
  toListingShortUrl,
} from '@zcode/nr-utils'
import type {
  ListableBuilding,
  ListableListing,
  Listable as ListableType,
  RawListable,
  Rentable as RentableType,
} from '@zcode/state-type'
import { shuffleArray } from '@zcode/utils'
import { displayAmenity, displayBuildingAmenity } from '../utils/amenity'
import * as Media from './media'
import Rentable from './rentable'
import { bedsBathsRangeTxt } from './rentable/utils/bedsBathsRangeTxt'

const DEFINITION = {
  address: 'address',
  agentId: 'agent_id',
  agentName: 'agent_name',
  amenities: 'amenities',
  amenityTags: 'amenity_tags',
  brokerageId: 'brokerage_id',
  brokerageName: 'brokerage_name',
  buildingAmenities: 'building_amenities',
  buildingAmenityTags: 'building_amenity_tags',
  buildingId: 'building_id',
  buildingName: 'building_name',
  city: 'city',
  cityState: 'city_state',
  supplementalDescription: 'supple_description',
  createdOn: 'created_on',
  description: 'description',
  distanceFromSearchCenter: 'distanceFromSearchCenter',
  external: 'external',
  externalRating: 'external_rating',
  externalUrl: (t: ListableType) =>
    t.isBuilding ? toExternalBuildingUrl(t.buildingId) : toExternalListingUrl(t.listingId),
  feedName: 'feed_name',
  floorplanCount: 'floorplan_count',
  groupId: 'group_id',
  id: 'listing_id',
  imageIds: 'image_ids',
  isMessageable: 'is_messageable',
  // NOTE: We are artificially inserting an is_featured prop in the listables
  // bizo. This property is used on ListItem (and related components) to add a
  // "featured" badge.
  isFeatured: 'is_featured',
  isFilterAd: 'is_filter_ad',
  // NOTE: We are artificially inserting an is_spotlight prop in the listables
  // bizo. This property is used to render a distinct, spotlight specific list card
  isSpotlight: 'is_spotlight',
  lat: 'lat',
  leaseType: 'lease_type',
  leasingFee: 'leasing_fee',
  listedOn: 'listed_on',
  listingId: 'listing_id',
  listingStatus: 'listing_status',
  listingType: 'listing_type',
  lng: 'lng',
  maxBathrooms: 'max_bathrooms',
  maxBedrooms: 'max_bedrooms',
  maxAdults: 'max_adults',
  maxChildren: 'max_children',
  maxLeaseDays: 'max_lease_days',
  maxSquareFeet: (t: ListableType) =>
    t._data.max_square_feet === undefined || t._data.max_square_feet < 0
      ? undefined
      : t._data.max_square_feet,
  maxPrice: 'max_price',
  minBathrooms: 'min_bathrooms',
  minBedrooms: 'min_bedrooms',
  minLeaseDays: 'min_lease_days',
  minPrice: 'min_price',
  previousPrice: 'previous_price',
  modifiedOn: 'modified_on',
  neighborhoodId: 'neighborhood_id',
  neighborhoodName: 'neighborhood_name',
  neighborhoodUrl: 'neighborhood_url',
  paShouldIndex: 'pa_should_index',
  paUrl: 'pa_url',
  pbId: 'pb_id',
  pbUrl: 'pb_url',
  pets: 'pets',
  phone: 'phone',
  plId: 'pl_id',
  plUrl: 'pl_url',
  promotion: 'promotion',
  propertyType: 'property_type',
  providerIdOrigin: 'provider_id',
  providerUrl: 'provider_url',
  review: 'review',
  reviewsSummary: 'reviews_summary',
  shortDescription: 'short_description',
  state: 'state',
  status: 'listing_status',
  title: 'title',
  tz: 'tz',
  viewings: { name: 'viewings', type: Viewing, isArray: true },
  zappable: 'zappable',
  zipcode: 'zipcode',
  // listing_statu for building listable doesn't get populated, when we fix that issue remove the
  // following line
  isActive: () => true as const,
  isFull: () => false,
  isHighestRated: (t: ListableType) => !!t.externalRating && t.externalRating >= 4.0,
  isLargeListCard: (t: ListableType) => !!t?.features?.includes(PropertyFeatures.LARGE_LIST_CARD),
  // pageHeading: (t) => t.buildingId ? getBuildingPageHeading(t) : undefined,
  images: (t: ListableType) => {
    if (t?.imageIds?.length) {
      return t.imageIds.map(
        (id) =>
          ({
            id,
            mediaType: MediaType.IMAGE,
            url: mediaUrl(id, Media.Size.XLARGE),
          }) as const
      )
    }
    return null
  },
  shortUrl: (t: ListableType) =>
    t.isBuilding ? toBuildingShortUrl(t.buildingId) : toListingShortUrl(t.listingId),
  // matches bubbleId impl of listing.js
  uId: ((t: ListableType) => {
    if (t.pbId) {
      return `b-p${t.pbId}`
    }
    if (t.buildingId) {
      return `b-${t.buildingId}`
    }
    if (t.plId) {
      return `l-${t.plId}p`
    }
    if (t.listingId) {
      return `l-${t.listingId}`
    }
    throw new Error('listable has neither listingId nor buildingId; we thought this was impossible')
  }) as ((t: ListableListing) => `l-${number}` | `l-${number}p`) &
    ((t: ListableBuilding) => `b-p${number}` | `b-${number}`),
  bedsRangeText: (t: ListableType) => {
    if (!t.buildingId) {
      return ''
    }
    if (t.maxBedrooms === -1) {
      return 'Rooms in'
    }

    const max =
      // @ts-expect-error maxBedrooms can't be Studio: we should remove `=== 'Studio'` check?
      t.maxBedrooms === 0 || t.maxBedrooms === 'Studio'
        ? ('Studio' as const)
        : (`${t.maxBedrooms} Bedroom${t.maxBedrooms === 1 ? '' : 's'}` as const)
    if (t.minBedrooms === -1) {
      return `Rooms\u2013${max}` as const
    }
    if (t.minBedrooms !== t.maxBedrooms) {
      // @ts-expect-error minBedrooms can't be Studio: we should remove `=== 'Studio'` check?
      const min = t.minBedrooms === 0 || t.minBedrooms === 'Studio' ? 'Studio' : t.minBedrooms
      return `${min}\u2013${max}` as const
    }

    return max
  },
  bedsRangeTxt: (t: ListableType) =>
    t.bedsRangeText.replace('Bedroom', 'Bed') as ListableType['bedsRangeTxt'],
  bedsRangeCountText: (t: ListableType) => {
    const { maxBedrooms, minBedrooms } = t
    if (maxBedrooms === minBedrooms) {
      return `${minBedrooms}`.replace('0', 'Studio')
    }
    return `${minBedrooms}-${maxBedrooms}`.replace('0', 'Studio')
  },
  bathsRangeCountText: (t: ListableType) => {
    const { maxBathrooms, minBathrooms } = t
    if (maxBathrooms === minBathrooms) {
      return `${maxBathrooms}`
    }
    return `${minBathrooms}-${maxBathrooms}`
  },
  bedsBathsRangeTxt,
  combinedAmenities: (
    t: Pick<ListableType, 'totalAmenitiesLength' | 'amenities' | 'buildingAmenities' | 'listingId'>
  ) => {
    if (!t.totalAmenitiesLength) {
      return []
    }
    // get amenity titles and remove undefineds and empty strings
    const amenities = (t.amenities ?? [])
      .map(displayAmenity)
      .filter(
        (amenityTitle): amenityTitle is EnumTitle<typeof ListingAmenity, ListingAmenity> =>
          !!amenityTitle
      )
    const buildingAmenities = (t.buildingAmenities ?? [])
      .map(displayBuildingAmenity)
      .filter(
        (amenityTitle): amenityTitle is EnumTitle<typeof BuildingAmenity, BuildingAmenity> =>
          !!amenityTitle
      )
    return shuffleArray(
      [
        // remove duplicates from building amenities
        ...new Set([...amenities, ...buildingAmenities]),
      ],
      t.listingId ?? 0
    )
  },
  combinedAmenityTags: (t: Pick<ListableType, 'amenityTags' | 'buildingAmenityTags'>) => {
    if (!t.amenityTags?.length && !t.buildingAmenityTags?.length) {
      return []
    }
    const listingAmenities = t?.amenityTags ?? []
    const buildingAmenities = t?.buildingAmenityTags ?? []

    return [...new Set([...buildingAmenities, ...listingAmenities])]
  },
  multiMessageSelected: 'multi_message_selected',
  totalAmenitiesLength: (t: ListableType) =>
    (t.amenities?.length ?? 0) + (t.buildingAmenities?.length ?? 0),
  isAirbnbFriendly: (t: ListableType) => !!t.features?.includes(PropertyFeatures.AIRBNB_FRIENDLY),
} as const

const Listable = autoclass<
  ListableType,
  any, // should be RawListable
  Bizo<ListableType, RawListable>,
  RentableType,
  object
>('Listable', class Listable extends Rentable {}, DEFINITION, null)

export default Listable
